import { Icon } from '@plone/volto/components';
import paintSVG from '@plone/volto/icons/paint.svg';
import heroSVG from '@plone/volto/icons/hero.svg';
import cardsSVG from '@plone/volto/icons/cards.svg';
import iconSVG from '@plone/volto/icons/select-all.svg';
import sliderSVG from '@plone/volto/icons/slider.svg';
import socialSVG from '@plone/volto/icons/share.svg';
import graphSVG from '@plone/volto/icons/slider.svg';
import blockThemes from './config/blockThemes';
import { injectEditThemeStyles } from './utils/injectEditThemeStyles';
import { Edit as CustomHeroEdit, View as CustomHeroView} from './components/Blocks/CustomHero';
import { Edit as ButtonRowEdit, View as ButtonRowView} from './components/Blocks/ButtonRow';
import { Edit as MultiCardEdit, View as MultiCardView } from './components/Blocks/MultiCardBlock';
import { Edit as IconLinkRowEdit, View as IconLinkRowView} from './components/Blocks/IconLinkRow';
import { Edit as EmblaCarouselEdit, View as EmblaCarouselView} from './components/Blocks/EmblaCarousel';
import { Edit as EmblaRatingsEdit, View as EmblaRatingsView } from './components/Blocks/EmblaRatings';
import { Edit as SocialMetaEdit, View as SocialMetaView } from './components/Blocks/SocialMeta';
import { Edit as HTMLBlockEdit, View as HTMLBlockView } from './components/Blocks/HTML';
import { Edit as GraphBlockEdit, View as GraphBlockView} from './components/Blocks/GraphBlock';
import SocialSharing from '@codesyntax/volto-social-sharing/SocialSharing';
import { DEFAULT_SOCIAL } from '@codesyntax/volto-social-sharing/defaultSettings';
import { faSquareXTwitter } from '@fortawesome/free-brands-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';

library.add(faSquareXTwitter);

const applyConfig = (config) => {
  config.settings.isMultilingual = false;
  config.settings.supportedLanguages = ['en'];
  config.settings.defaultLanguage = 'en';
  config.settings.favicon = '../public/favicon-v2.ico';

  config.blocks.themes = blockThemes;
  if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => injectEditThemeStyles(blockThemes),
        { once: true },
      );
    } else {
      injectEditThemeStyles(blockThemes);
    }
  }

  //Adding custom colours to Slate editor
  config.settings.slate.styleMenu.inlineStyles = [
    ...(config.settings.slate.styleMenu?.inlineStyles || []),
    {
      cssClass: 'text-coral',
      label: 'Coral Text',
      icon: () => (
        <Icon name={paintSVG} size="18px" color="var(--coral-base)" />
      ),
    },
    {
      cssClass: 'text-green',
      label: 'Green Text',
      icon: () => (
        <Icon name={paintSVG} size="18px" color="green" />
      ),
    },
    {
      cssClass: 'text-blue',
      label: 'Blue Text',
      icon: () => (
        <Icon name={paintSVG} size="18px" color="blue" />
      ),
    },
  ];
  
  config.settings.slate.styleMenu.blockStyles = [
    ...(config.settings.slate.styleMenu?.blockStyles || []),
    {
      cssClass: "circle-header",
      label: "Circle Header",
      nodeType: 'h3',
      icon: () => <Icon name={paintSVG} size="18px" />,
    },
    {
      cssClass: 'pre-header',
      label: 'Text above header',
      nodeType: 'paragraph', // Ensures it only applies to paragraph blocks
      icon: () => <Icon name={paintSVG} size="18px" />,
    },
    {
      cssClass: "center-text",
      label: "Center Text",
      icon: () => <Icon name={paintSVG} size="18px" />,
    },
  ];

  //CustomHero
  config.blocks.blocksConfig.customHero = {
    id: 'customHero',
    title: 'Hero Block',
    icon: heroSVG,
    group: 'common',
    view: CustomHeroView,
    edit: CustomHeroEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //ButtonRow
  config.blocks.blocksConfig.buttonRow = {
    id: 'buttonRow',
    title: 'Button Row',
    icon: cardsSVG,
    group: 'common',
    view: ButtonRowView,
    edit: ButtonRowEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //IconLinkRow
  config.blocks.blocksConfig.iconLinkRow = {
    id: 'iconLinkRow',
    title: 'Icon Link Row',
    icon: iconSVG,
    group: 'common',
    view: IconLinkRowView,
    edit: IconLinkRowEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //Multicard Block
  config.blocks.blocksConfig.multiCard = {
    id: 'multiCard',
    title: 'Multi Card',
    icon: cardsSVG,
    group: 'common',
    view: MultiCardView,
    edit: MultiCardEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //Embla Carousel
  config.blocks.blocksConfig.EmblaCarousel = {
    id: 'EmblaCarousel',
    title: 'Embla Carousel',
    icon: sliderSVG,
    group: 'common',
    view: EmblaCarouselView,
    edit: EmblaCarouselEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //Embla Ratings
  config.blocks.blocksConfig.EmblaRatings = {
    id: 'EmblaRatings',
    title: 'Embla Ratings',
    icon: sliderSVG,
    group: 'common',
    view: EmblaRatingsView,
    edit: EmblaRatingsEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //Social Meta Block
  config.blocks.blocksConfig.socialMeta = {
    id: 'socialMeta',
    title: 'Social Media Meta',
    icon: socialSVG,
    group: 'common',
    view: SocialMetaView,
    edit: SocialMetaEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  //HTML Block
  config.blocks.blocksConfig.html = {
    ...config.blocks.blocksConfig.html,
    view: HTMLBlockView,
    edit: HTMLBlockEdit,
    sidebarTab: 1,
    blockHasOwnFocusManagement: true,
  };

  //Graph Block
  config.blocks.blocksConfig.graphBlock = {
    id: 'graphBlock',
    title: 'Graph Block',
    icon: graphSVG,
    group: 'common',
    view: GraphBlockView,
    edit: GraphBlockEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

//Volto Social Sharing
  DEFAULT_SOCIAL.push({
    name: "LinkedIn",
    fa_name: ["fab", "linkedin-in"],
    color: "#0a66c2",
    sharing_url: "https://www.linkedin.com/sharing/share-offsite/?url=",
    id: "li"
  });
  let twitter = DEFAULT_SOCIAL.find((social) => social.id === 'tw');
  if (twitter) {
    twitter.fa_name = faSquareXTwitter;
    twitter.color = 'black';
    twitter.sharing_url = "https://x.com/intent/post?text=Check%20this%20out:&url=";
  ;
  }
  config.settings.appExtras = [
    ...config.settings.appExtras,
    {
      match: {
        path: ['**/news/**'],
        exact: false,
      },
      component: SocialSharing,
      props: {
        socialElements: DEFAULT_SOCIAL,
      },
    },
  ];

  return config;
};

export default applyConfig;
